import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  getImagesPageByProjectId,
  mapImageToCard,
} from "@/services/images/imageService";
import { sortImagesByRecency } from "@/utils/imageRecencySort";
import { deduplicateMergeById } from "@/utils/deduplicateById";
import { IMAGES_PAGE_SIZE } from "@/utils/paginationConstants";

/**
 * Carrega imagens de um projeto com paginação Firestore (updatedAt DESC).
 *
 * @param {string | undefined} projectId
 */
export function useProjectImages(projectId) {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);
  /** @type {React.MutableRefObject<import("@/services/firebase/dates").PaginationCursor | null>} */
  const cursorRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);

  const resetPaging = useCallback(() => {
    setImages([]);
    setHasMore(false);
    setLoadMoreError(null);
    cursorRef.current = null;
    loadMoreInFlightRef.current = false;
  }, []);

  const loadMore = useCallback(async () => {
    if (
      !projectId ||
      !user?.uid ||
      !hasMore ||
      loadMoreInFlightRef.current ||
      loadingInitial ||
      !cursorRef.current
    ) {
      return;
    }

    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);

    try {
      const result = await getImagesPageByProjectId(projectId, user.uid, {
        limitCount: IMAGES_PAGE_SIZE,
        cursor: cursorRef.current,
      });

      setImages((current) => deduplicateMergeById(current, result.items));
      cursorRef.current = result.cursor;
      setHasMore(result.hasMore);
    } catch (err) {
      setLoadMoreError(err);
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [projectId, user?.uid, hasMore, loadingInitial]);

  const addImage = useCallback((image) => {
    setImages((current) =>
      sortImagesByRecency([
        {
          ...image,
          updatedAt: image.updatedAt ?? new Date(),
        },
        ...current.filter((item) => item.id !== image.id),
      ]),
    );
  }, []);

  const updateImage = useCallback((imageId, updates) => {
    setImages((current) => {
      const existing = current.find((image) => image.id === imageId);

      if (!existing) {
        return current;
      }

      const updated = {
        ...existing,
        ...updates,
        updatedAt: updates.updatedAt ?? new Date(),
      };

      return sortImagesByRecency([
        updated,
        ...current.filter((image) => image.id !== imageId),
      ]);
    });
  }, []);

  const removeImage = useCallback((imageId) => {
    setImages((current) => current.filter((image) => image.id !== imageId));
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!projectId || !user?.uid) {
        if (!cancelled) {
          resetPaging();
          setLoadingInitial(false);
        }
        return;
      }

      if (!cancelled) {
        setLoadingInitial(true);
        setError(null);
        setLoadMoreError(null);
        cursorRef.current = null;
      }

      try {
        const result = await getImagesPageByProjectId(projectId, user.uid, {
          limitCount: IMAGES_PAGE_SIZE,
        });

        if (!cancelled) {
          setImages(result.items);
          cursorRef.current = result.cursor;
          setHasMore(result.hasMore);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          resetPaging();
        }
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [projectId, user?.uid, resetPaging]);

  const cardImages = useMemo(() => images.map(mapImageToCard), [images]);

  return {
    images,
    cardImages,
    loading: loadingInitial,
    loadingInitial,
    loadingMore,
    hasMore,
    error,
    loadMoreError,
    loadMore,
    addImage,
    updateImage,
    removeImage,
  };
}
